import { Component, Inject, Vue, Watch } from 'vue-property-decorator';
import { CountryRepository } from '@/domain/country/CountryRepository';
import { ProjectRepository } from '@/domain/project/ProjectRepository';
import { Logger } from '@/domain/Logger';
import { Store } from '@/primary/store/Store';
import { OLMap } from '@/primary/webmapping/common/OLMap';
import { OLLayer } from '@/primary/webmapping/common/OLLayer';
import { GeoJSON } from 'ol/format';
import { CountryRendering, toCountryRendering, toCountryRenderingFeature } from '@/primary/country/CountryRendering';
import { OLClickSelect } from '@/primary/webmapping/common/OLClickSelect';
import { Country } from '@/domain/country/Country';
import { CountryLabel } from '@/domain/country/CountryLabel';
import { CountryLabelRepository } from '@/domain/country/CountryLabelRepository';

@Component
export default class CountryMapComponent extends Vue {
  maxRank = -1;

  @Inject()
  countryOLMap!: () => OLMap;

  @Inject()
  countryOLSelect!: () => OLClickSelect;

  @Inject()
  countryOLLayer!: () => OLLayer<GeoJSON.MultiPolygon, CountryRendering>;

  @Inject()
  countryLabelOLLayer!: () => OLLayer<GeoJSON.Point, CountryLabel>;

  @Inject()
  countryRepository!: () => CountryRepository;

  @Inject()
  countryLabelRepository!: () => CountryLabelRepository;

  @Inject()
  projectRepository!: () => ProjectRepository;

  @Inject()
  store!: () => Store;

  @Inject()
  logger!: () => Logger;

  @Watch('projectsCountByCountry')
  projectsCountByCountryWatcher() {
    this.updateCountryFeatures();
  }

  get projectsCountByCountry() {
    return this.store().getProjectCountsByCountry();
  }

  created() {
    this.countryOLMap().onMoveEnd(() => this.handleLabels());
  }

  mounted() {
    Promise.all([this.countryRepository().list(), this.projectRepository().list()])
      .then(([countryFeatureCollection, projects]) => {
        this.store().saveProjects(projects);
        this.store().saveCountries(countryFeatureCollection.features.map(feature => feature.properties));
        this.countryOLMap().setTarget('country-map');
        this.setCountryFeatures(countryFeatureCollection);
      })
      .catch(this.error);
    this.handleLabels();
  }

  private error(error: Error) {
    this.logger().error('Fail to retrieve country or project', error);
  }

  private countryLabelError(error: Error) {
    this.logger().error('Fail to retrieve country label', error);
  }

  private getMaxRank() {
    const zoom = this.countryOLMap().getZoom();
    return zoom < 4 ? 3 : zoom < 5 ? 6 : 30;
  }

  private async handleLabels() {
    const maxRank = this.getMaxRank();
    if (maxRank !== this.maxRank) {
      this.maxRank = maxRank;
      this.listCountryLabel();
    }
  }

  private async listCountryLabel() {
    this.countryLabelRepository()
      .list(this.maxRank)
      .then(countryLabelFeatureCollection => this.countryLabelOLLayer().setFeatures(countryLabelFeatureCollection))
      .catch(this.countryLabelError);
  }

  private setCountryFeatures(countryFeatureCollection: GeoJSON.FeatureCollection<GeoJSON.MultiPolygon, Country>) {
    const features = countryFeatureCollection.features.map(feature => toCountryRenderingFeature(feature));
    this.countryOLLayer().setFeatures({ type: 'FeatureCollection', features });
  }

  private updateCountryFeatures() {
    const countryRenderings = this.store()
      .getCountries()
      .map(country => toCountryRendering(country, this.projectsCountByCountry));
    this.countryOLLayer().updateFeatures(countryRenderings);
  }
}
